import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { colors, radii } from '../theme/tokens';
import Icon from './ui/Icon';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({ message, ...options });
    });
  }, []);

  const handleConfirm = () => {
    setDialog(null);
    resolverRef.current?.(true);
  };

  const handleCancel = () => {
    setDialog(null);
    resolverRef.current?.(false);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div style={styles.overlay} onClick={handleCancel}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.iconWrap}>
              <span style={{
                ...styles.icon,
                background: dialog.danger ? 'rgba(229,84,75,0.14)' : 'rgba(47,182,92,0.14)'
              }}>
                <Icon name={dialog.icon || (dialog.danger ? 'warning' : 'help')} size={30}
                  color={dialog.danger ? colors.red : colors.parGreenBright} />
              </span>
            </div>
            <h3 style={styles.title}>{dialog.title || 'Confirm Action'}</h3>
            <p style={styles.message}>{dialog.message}</p>
            <div style={styles.buttons}>
              <button onClick={handleCancel} style={styles.cancelBtn}>
                {dialog.cancelText || 'Cancel'}
              </button>
              <button onClick={handleConfirm} style={{
                ...styles.confirmBtn,
                background: dialog.danger ? colors.red : colors.parGreen
              }}>
                {dialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
};

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: colors.overlay,
    zIndex: 99998,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backdropFilter: 'blur(2px)',
    animation: 'fadeIn 0.15s ease'
  },
  modal: {
    background: colors.menuBg,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.lg,
    padding: '32px 28px 24px',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    textAlign: 'center',
    color: colors.text,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    animation: 'slideUp 0.2s ease'
  },
  iconWrap: {
    marginBottom: '14px',
    display: 'flex',
    justifyContent: 'center'
  },
  icon: {
    width: 56, height: 56, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  title: {
    margin: '0 0 10px',
    fontSize: '18px',
    color: colors.text,
    fontWeight: '700'
  },
  message: {
    margin: '0 0 24px',
    fontSize: '15px',
    color: colors.textMuted,
    lineHeight: '1.5'
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center'
  },
  cancelBtn: {
    padding: '11px 24px',
    background: colors.glass,
    color: colors.text,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.pill,
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    flex: 1,
    maxWidth: '140px',
    transition: 'background 0.15s'
  },
  confirmBtn: {
    padding: '11px 24px',
    color: 'white',
    border: 'none',
    borderRadius: radii.pill,
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    flex: 1,
    maxWidth: '140px',
    transition: 'background 0.15s'
  }
};
